import { useState } from "react";
import type {
  UpsellPageDTO,
  SingleProductLayout,
  OfferProduct,
  QuizChartData,
} from "@/types/upsell.types.ts";
import { DiscountBadge } from "../molecules/DiscountBadge";
import { ShippingNotice } from "../sections/ShippingNotice";
import { Disclaimer } from "../sections/Disclaimer";
import parse from "html-react-parser";
import {
  buildRedirectUrl,
  normalizeUpsellProduct,
  useUpsellSubmit,
} from "@/hooks/useUpsellSubmit.ts";
import { useQuizChartData } from "@/hooks/useQuizChartData.ts";
import { replaceGoalWeightToken } from "@/utils/quizChart.ts";
import { WeightChart } from "../molecules/WeightChart";

type SingleSectionUpsellProps = {
  data: UpsellPageDTO;
};

function BenefitsList({ layout }: { layout: SingleProductLayout }) {
  const benefits = layout.benefits;
  if (!benefits || !Array.isArray(benefits) || benefits.length === 0)
    return null;

  return (
    <div className="bundle-benefits">
      {benefits.map((benefit, idx) => (
        <div className="benefit-item" key={idx}>
          {benefit.benefit_image && (
            <img
              src={benefit.benefit_image}
              alt={benefit.tr_title}
              className="benefit-graph"
            />
          )}
          <div className="benefit-content">
            <h4 className="benefit-title">{parse(benefit.tr_title)}</h4>
            <div className="benefit-subtitle">
              {parse(benefit.tr_description)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ChartContent({
  layout,
  chartData,
  isChartLoading,
}: {
  layout: SingleProductLayout;
  chartData: QuizChartData | null;
  isChartLoading: boolean;
}) {
  const fallbackChartImage = layout.chart_section.chart_image;
  const shouldRenderFallback =
    !chartData && !isChartLoading && !!fallbackChartImage;

  if (!chartData && !shouldRenderFallback) {
    return null;
  }

  return (
    <div
      className={`summary-section${shouldRenderFallback ? " summary-section--image-only" : ""}`}
    >
      {chartData ? (
        <>
          <h3 className="section-label">
            {layout.chart_section.tr_chart_label}
          </h3>
          <WeightChart
            currentWeight={chartData.currentWeight}
            goalWeight={chartData.goalWeight}
            unit={chartData.unit}
          />
          <div className="chart-legend">
            <div className="legend-item">
              <div className="legend-indicator green"></div>
              <span className="legend-text">
                {layout.chart_section.tr_legend_with_product}
              </span>
            </div>
            <div className="legend-item">
              <div className="legend-indicator red"></div>
              <span className="legend-text">
                {layout.chart_section.tr_legend_without_product}
              </span>
            </div>
          </div>
        </>
      ) : (
        <img
          src={fallbackChartImage ?? ""}
          alt="Chart"
          className="chart-image"
        />
      )}
    </div>
  );
}

function PricingCard({
  product,
  layout,
  purchaseType,
  linkDecline,
  hasSubscription,
  isSubmitting,
  onSubmit,
}: {
  product: OfferProduct;
  layout: SingleProductLayout;
  purchaseType: "onetime" | "subscribe";
  linkDecline: string;
  hasSubscription: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
}) {
  const isSubscribe = purchaseType === "subscribe" && hasSubscription;
  const price = isSubscribe ? product.subscr_price : product.price;
  const oldPrice = isSubscribe ? product.subscr_old_price : product.old_price;
  const buttonLabel = isSubmitting
    ? layout.tr_processing_text || "Processing"
    : layout.tr_pay_now;

  return (
    <div className="pricing-card">
      <div className="pricing-info">
        <div className="payment-type">{layout.tr_payment_type}</div>
        <div className="price-main">{price?.formatted}</div>
        {oldPrice?.value && (
          <div className="price-regular">
            {layout.tr_regular_price_label}:{" "}
            <span className="price-crossed">{oldPrice.formatted}</span>
          </div>
        )}
      </div>

      <div className="pricing-button">
        <button
          className="pay-btn"
          onClick={onSubmit}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {buttonLabel}
        </button>
      </div>

      <div className="mobile-decline-container">
        <a href={linkDecline} className="mobile-decline-btn">
          {layout.tr_decline_text}
        </a>
      </div>
    </div>
  );
}

export function SingleSectionUpsell({ data }: SingleSectionUpsellProps) {
  const [purchaseType, setPurchaseType] = useState<"onetime" | "subscribe">(
    "onetime",
  );

  const layout = data.single_product_layout;
  if (!layout) return null;

  const primaryProduct =
    data.offer_products.find((p) => p.is_primary) ?? data.offer_products[0];
  if (!primaryProduct) return null;

  const { isSubmitting, submitProducts } = useUpsellSubmit(data);
  const { chartData, isLoading: isChartLoading } = useQuizChartData();
  const subheadingHasGoalWeightToken =
    layout.tr_subheading.includes("###goal_weight###");
  const subheading =
    subheadingHasGoalWeightToken && isChartLoading
      ? ""
      : replaceGoalWeightToken(layout.tr_subheading, chartData);
  const linkDecline =
    buildRedirectUrl(
      data.upsell_links.link_decline || "",
      window.orderId || "",
    ) || "#";
  const hasSubscription = data.offer_products_has_subscription;
  const handleSubmit = () => {
    const normalizedBundleProducts = data.offer_products.map((product) =>
      normalizeUpsellProduct(product, purchaseType, hasSubscription),
    );
    const normalizedPrimaryProduct =
      normalizedBundleProducts.find((product) => product.is_primary) ??
      normalizedBundleProducts[0];

    submitProducts(normalizedBundleProducts, normalizedPrimaryProduct);
  };

  return (
    <main className="upsell-container">
      <ShippingNotice
        tr_shipping_message={layout.tr_shipping_message}
        shipping_threshold={layout.shipping_threshold}
        shipping_start_value={layout.shipping_start_value}
      />

      <DiscountBadge text={layout.tr_discount_badge} />

      <div className="split-layout content-wide">
        <div className="content-left">
          <section className="upsell-header">
            <h1 className="upsell-title">{parse(layout.tr_heading)}</h1>

            {subheading && (
              <div className="upsell-subtitle">{parse(subheading)}</div>
            )}
          </section>

          {layout.hero_image && (
            <div className="product-image-container mobile-only">
              <img
                src={layout.hero_image || ""}
                alt={primaryProduct.product_name}
                className="product-image"
              />
            </div>
          )}

          {layout.hero_content_type === "chart" && (
            <ChartContent
              layout={layout}
              chartData={chartData}
              isChartLoading={isChartLoading}
            />
          )}

          {layout.hero_content_type === "benefits_list" && (
            <BenefitsList layout={layout} />
          )}

          {hasSubscription && (
            <div className="purchase-selector-wrapper">
              <div className="purchase-selector">
                <button
                  className={`selector-btn${purchaseType === "onetime" ? " active" : ""}`}
                  data-type="onetime"
                  onClick={() => setPurchaseType("onetime")}
                >
                  {layout.tr_onetime_label}
                </button>
                <button
                  className={`selector-btn${purchaseType === "subscribe" ? " active" : ""}`}
                  data-type="subscribe"
                  onClick={() => setPurchaseType("subscribe")}
                >
                  {layout.tr_subscribe_label}
                  {layout.tr_subscribe_badge && (
                    <span className="selector-badge">
                      {layout.tr_subscribe_badge}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          <PricingCard
            product={primaryProduct}
            layout={layout}
            purchaseType={purchaseType}
            linkDecline={linkDecline}
            hasSubscription={hasSubscription}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />

          <div className="decline-container">
            <a href={linkDecline} className="decline-btn">
              {layout.tr_decline_text}
            </a>
          </div>
        </div>

        {layout.hero_image && (
          <div className="content-right desktop-only">
            <div className="product-image-container">
              <img
                src={layout.hero_image || ""}
                alt={primaryProduct.product_name}
                className="product-image"
              />
            </div>
          </div>
        )}
      </div>

      {layout.tr_disclaimer && (
        <Disclaimer tr_disclaimer={layout.tr_disclaimer} />
      )}
    </main>
  );
}
